import { inject, Injectable, NgZone } from '@angular/core';
import { CompiereNotification } from '@compiere-ws/models/compiere-notification-json';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import * as AGChannel from 'ag-channel';
import { environment } from 'environments/environment';
import { from, Observable, Subscription } from 'rxjs';
import { AGClientSocket, create } from 'socketcluster-client';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  static BROADCAST_NOTIFICATION_CHANNEL = 'notifications';
  static PERSONAL_NOTIFICATION_CHANNEL = '';
  static ROLE_NOTIFICATION_CHANNEL = '';
  static PROCESS_IN_PROGRESS_CHANNEL = '';
  static RELOAD_CONFIG_CHANNEL = 'reload_config';

  private smService = inject(SecurityManagerService);
  readonly #zone = inject(NgZone);

  /**
   * Channel messages as an observable. The socket lives outside Angular (its pings and reconnect timers ran a check
   * of the whole app each); a message enters Angular for its subscribers, unless outsideAngular (they enter it
   * themselves when something changes).
   */
  #observe<T>(iterable: any, outsideAngular = false): Observable<T> {
    return new Observable<T>((subscriber) => {
      const run = (fn: () => void) => (outsideAngular ? fn() : this.#zone.run(fn));
      const inner = this.#zone.runOutsideAngular(() =>
        from(iterable as AsyncIterable<T>).subscribe({
          next: (value) => run(() => subscriber.next(value)),
          error: (error) => run(() => subscriber.error(error)),
          complete: () => run(() => subscriber.complete()),
        })
      );
      return () => inner.unsubscribe();
    });
  }

  private socket: AGClientSocket;

  private broadcastNotificationsChannel: AGChannel<CompiereNotification>;
  private personalNotificationsChannel: AGChannel<CompiereNotification>;
  private roleNotificationsChannel: AGChannel<CompiereNotification>;
  private processInProgressChannel: AGChannel<any>;

  private errorSub: Subscription;

  #subData: () => any = () => {
    const account = this.smService.getIupicsUserAccount();
    return {
      data: {
        from: 'frontend',
        ad_user_id: account.id,
        ad_role_id: account.current_role.role_id,
        ad_org_id: this.smService.getIupicsUserContext()['#AD_Org_ID'],
      },
    };
  };

  //#region init

  /**
   * Initialise le socket
   */
  initSocket(): void {
    const hostname = environment.config.backend.socketcluster.hostname.replace(/http:\/\/|https:\/\//g, '');
    const port = environment.config.backend.socketcluster.port;
    const secure = environment.config.backend.socketcluster.secure;

    this.socket =
      this.socket === undefined
        ? this.#zone.runOutsideAngular(() =>
            create({
              hostname: hostname,
              port: port,
              secure: secure,
              autoReconnectOptions: {
                initialDelay: 3000,
                randomness: 0,
              },
            })
          )
        : this.socket;
    this.putSocketConnectionToWindow();
    this.handleError();
  }
  //#endregion

  //#region broadcast

  /**
   * Active les notifications propagées
   */
  enableBroadcastNotifications(): Observable<CompiereNotification> {
    if (this.broadcastNotificationsChannel === undefined) {
      this.broadcastNotificationsChannel = this.socket.subscribe(
        SocketService.BROADCAST_NOTIFICATION_CHANNEL,
        this.#subData()
      );
      this.putSocketConnectionToWindow();
    }
    return this.#observe(this.broadcastNotificationsChannel);
  }

  /**
   * Désactive les notifications propagées
   */
  disableBroadcastNotifications(): void {
    if (this.broadcastNotificationsChannel) {
      this.broadcastNotificationsChannel.kill();
      this.broadcastNotificationsChannel.unsubscribe();
      this.broadcastNotificationsChannel = undefined;
    }
  }

  //#endregion

  //#region personal

  /**
   * Active les notifications personnelles
   */
  enablePersonalNotifications(): Observable<CompiereNotification> {
    if (this.personalNotificationsChannel === undefined) {
      SocketService.PERSONAL_NOTIFICATION_CHANNEL = String(this.smService.getIupicsUserAccount().id);
      this.personalNotificationsChannel = this.socket.subscribe(
        SocketService.PERSONAL_NOTIFICATION_CHANNEL,
        this.#subData()
      );
      this.putSocketConnectionToWindow();
    }
    return this.#observe(this.personalNotificationsChannel);
  }

  /**
   * Désactive les notifications personnelles
   */
  disablePersonalNotifications(): void {
    if (this.personalNotificationsChannel) {
      this.personalNotificationsChannel.kill();
      this.personalNotificationsChannel.unsubscribe();
      this.personalNotificationsChannel = undefined;
    }
  }

  //#endregion

  //#region role

  /**
   * Active les notifications pour le rôle courant
   */
  enableRoleNotifications(role_id?: number): Observable<CompiereNotification> {
    if (this.roleNotificationsChannel === undefined) {
      role_id =
        role_id === undefined
          ? this.smService.getIupicsUserAccount().roles.find((role) => role.isSelected).role_id
          : role_id;
      SocketService.ROLE_NOTIFICATION_CHANNEL = `${this.smService.getIupicsUserAccount().id}_${role_id}`;
      this.roleNotificationsChannel = this.socket.subscribe(SocketService.ROLE_NOTIFICATION_CHANNEL, this.#subData());
      this.putSocketConnectionToWindow();
    }
    return this.#observe(this.roleNotificationsChannel);
  }

  /**
   * Désactive les notifications pour le rôle courant
   */
  disableRoleNotifications(): void {
    if (this.roleNotificationsChannel) {
      this.roleNotificationsChannel.kill();
      this.roleNotificationsChannel.unsubscribe();
      this.roleNotificationsChannel = undefined;
    }
  }

  //#endregion

  //#region processInProgressChannel

  /** outsideAngular: messages are not brought into Angular (the subscriber does it when it changes something) */
  enableProcessInProgressChannel(outsideAngular = false): Observable<any> {
    if (this.processInProgressChannel === undefined) {
      const me = this.smService.getIupicsUserAccount();

      if (me.current_role.role_id === 0) {
        // * systemAdmin: processRunning
        SocketService.PROCESS_IN_PROGRESS_CHANNEL = `processRunning`;
      } else if (me.current_role.isAdministrator) {
        // * administrator: processRunning_clientID
        const clientID = this.smService.getIupicsUserContext()['#AD_Client_ID'];
        SocketService.PROCESS_IN_PROGRESS_CHANNEL = `processRunning_${clientID}`;
      } else {
        // * other: processRunning_userID_roleID
        SocketService.PROCESS_IN_PROGRESS_CHANNEL = `processRunning_${me.id}_${me.current_role.role_id}`;
      }
      this.processInProgressChannel = this.socket.subscribe(SocketService.PROCESS_IN_PROGRESS_CHANNEL, this.#subData());
    }

    return this.#observe(this.processInProgressChannel, outsideAngular);
  }

  /**
   * Désactive les notifications pour le rôle courant
   */
  disableProcessInProgressChannel(): void {
    if (this.processInProgressChannel) {
      this.processInProgressChannel.kill();
      this.processInProgressChannel.unsubscribe();
      this.processInProgressChannel = undefined;
    }
  }

  //#endregion

  //#region data

  /**
   * Ouvre un channel de données
   * @param {string} channel name of the channel
   * @returns {any} channel data
   */
  openDataChannel<T>(channel: string): Observable<T> {
    return this.#observe(this.socket.subscribe(channel, this.#subData()));
  }

  /**
   * Ferme un canal de reception de données
   * @param {string} channel
   */
  closeDataChannel(channel: string): void {
    this.socket.killChannel(channel);
    this.socket.unsubscribe(channel);
  }

  //#endregion

  //#region reloadConfig

  /**
   * @returns {any} channel reload_config
   */
  enableReloadConfigChannel(): Observable<any> {
    return this.#observe(this.socket.subscribe(SocketService.RELOAD_CONFIG_CHANNEL, this.#subData()));
  }

  /**
   * Ferme le channel de rechargement de config
   */
  disableReloadConfigChannel(): void {
    this.socket.killChannel(SocketService.RELOAD_CONFIG_CHANNEL);
    this.socket.unsubscribe(SocketService.RELOAD_CONFIG_CHANNEL);
  }

  //#endregion

  //#region misc

  /**
   * Broadcast des datas sur un channel spécifique
   * @param {string} channel
   * @param {?any} data
   */
  broadcast(channel: string, data?: any): void {
    this.socket.transmitPublish(channel, data);
  }

  /**
   * Retourne l'id du socket ouvert
   * @returns {string} Socket Id
   */
  getSocketId(): string {
    return this.socket.id;
  }

  /**
   * Gère les erreurs
   */
  handleError(): void {
    this.errorSub?.unsubscribe();
    this.errorSub = from(this.socket.listener('error')).subscribe(({ error }) => {
      console.error(error);
    });
  }

  //#endregion

  /** logout: every channel closed and the connection dropped (a later login creates a new one) */
  disconnect(): void {
    this.disableBroadcastNotifications();
    this.disablePersonalNotifications();
    this.disableRoleNotifications();
    this.disableProcessInProgressChannel();
    this.errorSub?.unsubscribe();
    this.errorSub = undefined;
    this.socket?.disconnect();
    this.socket = undefined;
    delete window['apiz_socket'];
  }

  putSocketConnectionToWindow() {
    window['apiz_socket'] = {
      broadcastNotificationsChannel: this.broadcastNotificationsChannel,
      personalNotificationsChannel: this.personalNotificationsChannel,
      roleNotificationsChannel: this.roleNotificationsChannel,
      socket: this.socket,
    };
  }

  // Method to send stop signal via WebSocket
  /** rejects when the stop signal could not be sent (the caller tells the user) */
  async stopProcess(channel: string): Promise<void> {
    await this.socket.transmitPublish('processRunning', channel); // Send the stop signal
  }
}
